/** Add / edit exercise modals, including the metric-change flow. */
import * as Sync from '../sync';
import { input, must } from '../dom';
import {
  currentExercise,
  exercises,
  refreshAllViews,
  saveLocal,
  setCurrentExercise,
  workoutData,
} from '../lib/store';
import { localStamp, type Exercise, type MetricKey } from '../types';

function checkedMetrics(prefix: string): MetricKey[] {
  const metrics: MetricKey[] = [];
  if (input(`${prefix}track-reps`).checked) metrics.push('reps');
  if (input(`${prefix}track-weight`).checked) metrics.push('weight');
  if (input(`${prefix}track-time`).checked) metrics.push('time');
  return metrics;
}

export function showAddExerciseModal(): void {
  must('add-exercise-modal').style.display = 'block';
  input('track-reps').checked = true;
  input('track-weight').checked = false;
  input('track-time').checked = false;
}

export function hideAddExerciseModal(): void {
  must('add-exercise-modal').style.display = 'none';
  input('new-exercise-name').value = '';
}

export function addNewExercise(): void {
  const name = input('new-exercise-name').value.trim().toLowerCase();

  if (!name) {
    alert('Please enter an exercise name');
    return;
  }
  if (exercises.some((ex) => ex.name === name)) {
    alert('Exercise already exists');
    return;
  }

  const metrics = checkedMetrics('');
  if (metrics.length === 0) {
    alert('Please select at least one metric to track');
    return;
  }

  exercises.push(Sync.stampExercise({ id: '', name, metrics, clientUpdated: localStamp('') }));
  workoutData[name] = [];
  saveLocal();

  refreshAllViews();
  hideAddExerciseModal();
}

export function showEditExerciseModal(): void {
  const exercise = exercises.find((ex) => ex.name === currentExercise);
  if (!exercise) return;

  input('edit-exercise-name').value = currentExercise;
  input('edit-track-reps').checked = exercise.metrics.includes('reps');
  input('edit-track-weight').checked = exercise.metrics.includes('weight');
  input('edit-track-time').checked = exercise.metrics.includes('time');
  must('edit-exercise-modal').style.display = 'block';
}

export function hideEditExerciseModal(): void {
  must('edit-exercise-modal').style.display = 'none';
}

/** Renames in place, moving the history to the new key and re-stamping it. */
function renameExercise(exercise: Exercise, newName: string): void {
  const moved = workoutData[exercise.name] ?? [];
  delete workoutData[exercise.name];
  workoutData[newName] = moved;
  for (const workout of moved) {
    workout.exercise = newName;
    Sync.stampWorkout(workout);
  }
  exercise.name = newName;
  setCurrentExercise(newName);
}

export function updateExercise(): void {
  const newName = input('edit-exercise-name').value.trim().toLowerCase();
  const newMetrics = checkedMetrics('edit-');

  if (newMetrics.length === 0) {
    alert('Please select at least one metric to track');
    return;
  }
  if (!newName) {
    alert('Please enter an exercise name');
    return;
  }
  if (newName !== currentExercise && exercises.some((ex) => ex.name === newName)) {
    alert('An exercise with this name already exists');
    return;
  }

  const exercise = exercises.find((ex) => ex.name === currentExercise);
  if (!exercise) return;

  const addingMetrics = newMetrics.some((metric) => !exercise.metrics.includes(metric));
  const hasHistory = (workoutData[currentExercise] ?? []).length > 0;

  if (addingMetrics && hasHistory) {
    input('edit-exercise-name').dataset['newName'] = newName;
    showMetricUpdateOptions(newMetrics);
    return;
  }

  if (newName !== currentExercise) renameExercise(exercise, newName);

  exercise.metrics = newMetrics;
  Sync.stampExercise(exercise);
  saveLocal();

  refreshAllViews();
  hideEditExerciseModal();
  alert('Exercise updated successfully!');
}

function showMetricUpdateOptions(newMetrics: MetricKey[]): void {
  must('new-metrics-list').textContent = newMetrics.join(', ');
  must('current-exercise-name').textContent = currentExercise;
  hideEditExerciseModal();
  must('metric-update-modal').style.display = 'block';
}

export function hideMetricUpdateModal(): void {
  must('metric-update-modal').style.display = 'none';
}

export function updateExistingExercise(): void {
  const newMetrics = checkedMetrics('edit-');
  const newName = input('edit-exercise-name').dataset['newName'] ?? currentExercise;
  const exercise = exercises.find((ex) => ex.name === currentExercise);
  if (!exercise) return;

  if (newName !== currentExercise) renameExercise(exercise, newName);

  exercise.metrics = newMetrics;
  Sync.stampExercise(exercise);
  saveLocal();

  refreshAllViews();
  hideMetricUpdateModal();
  alert(`Exercise updated! Future workouts will track: ${newMetrics.join(', ')}`);
}

export function createNewVariant(): void {
  const entered = prompt(
    'Enter name for the new exercise variant:',
    `${currentExercise} (weighted)`,
  );
  if (!entered?.trim()) return;

  const finalName = entered.trim().toLowerCase();
  if (exercises.some((ex) => ex.name === finalName)) {
    alert('An exercise with this name already exists');
    return;
  }

  exercises.push(
    Sync.stampExercise({
      id: '',
      name: finalName,
      metrics: checkedMetrics('edit-'),
      clientUpdated: localStamp(''),
    }),
  );
  workoutData[finalName] = [];
  setCurrentExercise(finalName);
  saveLocal();

  refreshAllViews();
  hideMetricUpdateModal();
  alert(`New exercise variant created: ${entered}`);
}
